
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronLeft } from 'lucide-react';
import DateTimeSelector from '@/components/booking/DateTimeSelector';
import BookingDetails from '@/components/payment/BookingDetails';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { getAvatarUrl } from '@/utils/storage';
import { detectUserTimezone } from '@/utils/timezone-detection';
import { TimezoneWarning } from '@/components/booking/TimezoneWarning';
import { STANDARD_SESSION_FORMATS } from '@/utils/session-format-helpers';

interface BookingSessionInfo {
  practitionerId: string;
  practitionerName: string;
  serviceId: string;
  serviceName: string;
  price: number;
  currency: string;
  duration: number;
  professionalTitle?: string;
  avatarUrl?: string;
  returnToPath?: string;
  practitionerTimezone?: string;
}

const BookingDateTime = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('booking');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [bookingInfo, setBookingInfo] = useState<BookingSessionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userTimezone] = useState(() => detectUserTimezone());

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load booking info from session storage
  useEffect(() => {
    const storedBookingInfo = sessionStorage.getItem('tempBookingInfo');
    console.log("Raw stored booking info:", storedBookingInfo);
    
    if (storedBookingInfo) {
      try {
        const parsedInfo = JSON.parse(storedBookingInfo);
        console.log("Parsed booking info:", parsedInfo);
        
        // Validate required fields
        if (!parsedInfo.practitionerId || !parsedInfo.serviceId) {
          throw new Error("Missing required booking information");
        }
        
        setBookingInfo(parsedInfo);
      } catch (error) {
        console.error('Error parsing booking info:', error);
        toast({
          variant: "destructive",
          title: t('invalidBookingInfo'),
          description: t('startBookingAgain')
        });
        navigate('/book-an-appointment');
      }
    } else {
      console.error("No tempBookingInfo found in sessionStorage");
      toast({
        variant: "destructive",
        title: t('noBookingInfo'),
        description: t('selectServiceFirst')
      });
      navigate('/book-an-appointment');
    }
  }, [navigate, toast, t]);

  const handleContinueToPayment = () => {
    if (!selectedDate || !selectedTime) {
      toast({
        variant: "destructive",
        title: t('selectDateTime'),
        description: t('dateTimeRequired')
      });
      return;
    }

    if (!bookingInfo) {
      toast({
        variant: "destructive",
        title: t('missingBookingInfo'),
        description: t('startBookingAgain')
      });
      navigate('/book-an-appointment');
      return;
    }

    setIsLoading(true);

    // Generate a unique booking ID
    const bookingId = crypto.randomUUID();

    // Store complete booking information for payment page
    const completeBookingInfo = {
      bookingId: bookingId,
      practitionerId: bookingInfo.practitionerId,
      practitionerName: bookingInfo.practitionerName,
      service: bookingInfo.serviceName,
      serviceId: bookingInfo.serviceId,
      price: bookingInfo.price,
      currency: bookingInfo.currency,
      duration: bookingInfo.duration,
      selectedDate: selectedDate.toISOString(),
      selectedTime: selectedTime,
      format: STANDARD_SESSION_FORMATS.VIRTUAL, // Default to virtual for online sessions
      practitionerTimezone: bookingInfo.practitionerTimezone || 'America/New_York'
    };

    console.log("Storing complete booking info for payment:", completeBookingInfo);
    sessionStorage.setItem('bookingInfo', JSON.stringify(completeBookingInfo));
    
    // Keep temporary booking info for potential back navigation
    // sessionStorage.removeItem('tempBookingInfo');

    // Navigate to payment page
    navigate('/payment');
  };

  const handleGoBack = () => {
    if (bookingInfo?.returnToPath) {
      navigate(bookingInfo.returnToPath);
    } else if (bookingInfo?.practitionerId) {
      navigate(`/book-an-appointment/${bookingInfo.practitionerId}`);
    } else {
      navigate(-1);
    }
  };
  // Transform booking info for BookingDetails component
  const transformBookingInfoForDisplay = (info: BookingSessionInfo) => {
    let displayDateTime = selectedDate || new Date();

    // If a time is selected, combine date and time
    if (selectedTime && selectedDate) {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      displayDateTime = new Date(selectedDate);
      displayDateTime.setHours(hours, minutes, 0, 0);
    }

    // Process avatar URL properly
    let processedAvatarUrl = null;
    if (info.avatarUrl) {
      // If it's already a full URL, use it directly
      if (info.avatarUrl.startsWith('http') || info.avatarUrl.startsWith('data:')) {
        processedAvatarUrl = info.avatarUrl;
      } else {
        // If it's a file path, convert it to a public URL
        processedAvatarUrl = getAvatarUrl('practitioner.photo', info.avatarUrl);
      }
    }

    return {
      practitioner: {
        first_name: info.practitionerName.split(' ')[0] || 'Unknown',
        last_name: info.practitionerName.split(' ').slice(1).join(' ') || 'Practitioner',
        professional_title: info.professionalTitle || 'Spiritual Practitioner',
        avatar_url: processedAvatarUrl
      },
      service: {
        service_name: info.serviceName,
        duration_minutes: info.duration,
        price: info.price,
        currency: info.currency
      },
      dateTime: displayDateTime,
      format: 'online' as const,
      location: undefined,
      notes: undefined,
      practitionerTimezone: info.practitionerTimezone || 'America/New_York'
    };
  };

  if (!bookingInfo) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-amber-50/50 pt-16 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
              <div className="bg-white rounded-lg p-6">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-amber-50/50 pt-16 pb-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-6"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {t('backToPractitioner')}
          </Button>

          <div className="grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <TimezoneWarning 
                userTimezone={userTimezone}
                practitionerTimezone={bookingInfo.practitionerTimezone || 'America/New_York'} 
              />
              
              <DateTimeSelector
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                selectedTime={selectedTime}
                onTimeChange={setSelectedTime}
                duration={bookingInfo.duration}
                practitionerId={bookingInfo.practitionerId}
                userTimezone={userTimezone}
              />

              <Card>
                <CardContent className="pt-6">
                  <Button 
                    onClick={handleContinueToPayment}
                    className="w-full"
                    disabled={!selectedDate || !selectedTime || isLoading}
                  >
                    {isLoading ? t('processing') : t('continueToPay')}
                  </Button>
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-1">
              <BookingDetails 
                bookingInfo={transformBookingInfoForDisplay(bookingInfo)}
              />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BookingDateTime;
